import { NgIf } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { EditorComponent } from "@tinymce/tinymce-angular";
import { environment } from "projects/assist/src/environments/environment";
import { Editor, RawEditorOptions, Ui } from "tinymce";
import { ModalComponent } from "../../../components/layout/modal/modal.component";
import { BasicDto } from "../../../models/basicDto";
import { FormControlElement } from "../../models/form-control-element";
import { UploadService } from "../../services/upload.service";
import { HorizontalWrapperComponent } from "../horizontal-wrapper/horizontal-wrapper.component";
import { ValidatorComponent } from "../validator/validator.component";

@Component({
  selector: "app-rich-text",
  templateUrl: "./rich-text.component.html",
  styleUrls: ["./rich-text.component.scss"],
  standalone: true,
  imports: [
    HorizontalWrapperComponent,
    NgIf,
    EditorComponent,
    ReactiveFormsModule,
    ValidatorComponent,
    ModalComponent,
  ],
})
export class RichTextComponent extends FormControlElement implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() label: string = "";
  @Input() maxHeight: number = 300;
  @Input() minHeight: number = 300;
  @Input() toolbar: string[] = [
    "fontfamily fontsize | h1 h2 h3 h4 | bold italic underline forecolor backcolor | alignleft aligncenter | link bullist numlist image table pagebreak | removeformat code",
  ];
  @Input() alignRight: boolean = false;
  @Input() noCols?: boolean;
  @Input() id: string = "";
  @Input() variables!: string[] | undefined;
  @Input() linkVariables!: string[] | undefined;
  @Input() rawLinks?: BasicDto<string>[];
  @Input() contentCss: string = "default";

  @Input() bodyBackgroundColorControl = new FormControl();
  @Input() bodyTextColorControl = new FormControl();

  @Input() readOnly = false;

  @Input() customButtons?: {
    name: string;
    spec: (editor: Editor) => Ui.Toolbar.ToolbarMenuButtonSpec;
  }[];

  @Input() modal: boolean = false;
  @Input() modalTitle: string = "Meer info"; // titel van de modal
  @Input() modalInfo!: HTMLDivElement; //  inhoud van de modal
  @ViewChild("content") content!: ElementRef;
  @ViewChild("editor") editor?: EditorComponent;
  config?: RawEditorOptions;

  @Input() forceShowVariablesButton = false;
  protected variableItems: any[] = [];
  protected renderEditor = false;
  private domObserver?: MutationObserver;

  constructor(
    private modalService: NgbModal,
    protected uploadService: UploadService,
    private el: ElementRef,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["variables"]) {
      this.updateVariables();
    }
    if (changes["maxHeight"] && this.config) {
      this.config.max_height = this.maxHeight;
    }
  }

  updateVariables() {
    this.variableItems = this.variables
      ? this.variables?.map((variable: string) => ({
          type: "menuitem",
          text: `{${variable}}`,
          onAction: () => {
            this.editor?.editor.insertContent(`{${variable}}`);
          },
        }))
      : [];
  }

  ngOnInit(): void {
    var darkmode = localStorage.getItem("darkMode") === "true";
    if (!this.config) {
      if ((this.variables && this.variables.length > 0) || this.forceShowVariablesButton) {
        //add to latest string in toolbar
        this.toolbar[this.toolbar.length - 1] += " variables";
        //this.toolbar.push('variables');
      }

      if (
        (this.linkVariables && this.linkVariables.length > 0) ||
        (this.rawLinks && this.rawLinks.length > 0)
      ) {
        //this.toolbar.push('linkVariables');
        this.toolbar[this.toolbar.length - 1] += " linkVariables";
      }
      this.customButtons?.forEach((customButton) => {
        this.toolbar[this.toolbar.length - 1] += " " + customButton.name;
      });

      const linkVariables = this.linkVariables;
      const customButtons = this.customButtons;
      const rawLinks = this.rawLinks;
      this.updateVariables();
      this.config = {
        base_url: "/tinymce",
        suffix: ".min",
        license_key: "gpl",
        promotion: false,
        menubar: false,
        plugins: ["link", "anchor", "lists", "code", "table", "image", "media", "pagebreak"],
        content_css: this.contentCss,
        content_style:
          "h3, h4 { margin-top: 0rem; margin-bottom: 0.25rem; } ul { margin: 0; padding: 0rem 0rem 0rem 1rem; }",
        toolbar: this.toolbar,
        pagebreak_separator: '<span style="page-break-after: always;">&nbsp;</span>',
        statusbar: false,
        min_height: this.minHeight,
        max_height: this.maxHeight,
        sandbox_iframes_exclusions: [
          "youtube.com",
          "youtu.be",
          "vimeo.com",
          "player.vimeo.com",
          "dailymotion.com",
          "embed.music.apple.com",
          "open.spotify.com",
          "giphy.com",
          "dai.ly",
          "codepen.io",
          "indd.adobe.com",
        ],
        extended_valid_elements: "iframe[*]",
        images_upload_url: "https://app.assistonline.eu/api/upload/tinymce?maxwidth=500",
        images_upload_credentials: true,
        images_upload_handler: (blobInfo: any, progress: any) => {
          const formData = new FormData();
          formData.append("file", blobInfo.blob(), blobInfo.filename());

          return new Promise((resolve, reject) => {
            this.uploadService.uploadTinymce(formData).subscribe(
              (res: any) => {
                // Neem aan dat de server een veld 'location' teruggeeft met de URL van de afbeelding
                resolve(`${environment.imageUrl}/Dba/vzffiles/tinymce/${res.location}`);
              },
              (error) => {
                reject("Upload failed: " + error.message);
              }
            );
          });
        },
        setup: (editor: Editor) => {
          editor.ui.registry.addMenuButton("variables", {
            text: "Variabele waarde",
            fetch: (callback: any) => {
              callback(this.variableItems);
            },
          });
          editor.ui.registry.addMenuButton("linkVariables", {
            text: "Links",
            fetch: function (callback: any) {
              let items = linkVariables
                ? linkVariables.map((variable: string) => ({
                    type: "menuitem",
                    text: `{${variable}}`,
                    onAction: function () {
                      editor.insertContent(`{${variable}}`);
                    },
                  }))
                : [];
              var rawItems = rawLinks
                ? rawLinks.map((variable: BasicDto<string>) => ({
                    type: "menuitem",
                    text: `${variable.Name}`,
                    onAction: function () {
                      editor.insertContent(
                        `<a href="${variable.Id}" target="_blank" rel="noopener">${variable.Name}</a>`
                      );
                    },
                  }))
                : [];
              items = items.concat(rawItems);
              callback(items);
            },
          });
          customButtons?.forEach((customButton) => {
            editor.ui.registry.addMenuButton(customButton.name, customButton.spec(editor));
          });
        },
      };
      if (darkmode) {
        this.config.skin = "oxide-dark";
        this.config.content_css = "dark";
      }
    }

    this.bodyBackgroundColorControl.valueChanges.subscribe((newColor) => {
      this.setBodyBackgroundColor(newColor);
    });
    this.bodyTextColorControl.valueChanges.subscribe((newColor) => {
      this.setBodyTextColor(newColor);
    });
    setTimeout(() => {
      this.setBodyBackgroundColor(this.bodyBackgroundColorControl.value);
      this.setBodyTextColor(this.bodyTextColorControl.value);
    }, 200);
  }

  setBodyBackgroundColor(color: string) {
    if (!this.readOnly && this.editor?.editor && this.editor?.editor.getBody()) {
      this.editor.editor.getBody().style.backgroundColor = color;
    }
  }

  setBodyTextColor(color: string) {
    if (!this.readOnly && this.editor?.editor && this.editor?.editor.getBody()) {
      this.editor.editor.getBody().style.color = color;
    }
  }

  ngAfterViewInit(): void {
    if (this.el.nativeElement.isConnected) {
      this.renderEditor = true;
      this.cdRef.detectChanges();
    } else {
      this.domObserver = new MutationObserver(() => {
        if (this.el.nativeElement.isConnected) {
          this.renderEditor = true;
          this.cdRef.detectChanges();
          this.domObserver?.disconnect();
          this.domObserver = undefined;
        }
      });
      this.domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  ngOnDestroy(): void {
    this.domObserver?.disconnect();
  }

  open(content: any) {
    console.log(this.content);
    this.modalService.open(content, {
      ariaLabelledBy: "modal-basic-title",
      centered: true,
      size: "lg",
    });
  }

  handleKeyDown(event: KeyboardEvent, editor: any) {
    if (event.keyCode == 9 && !event.shiftKey) {
      // tab pressed
      editor.insertContent("&emsp;&emsp;"); // inserts tab
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    return true;
  }
}
